import {
  Component,
  OnInit,
  SecurityContext,
  TemplateRef,
  inject,
  model,
  viewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserOperation } from '../../../../../../core/enums/user-operation';
import { ShiveDialogService } from '../../../../../../core/services/controls/shive-dialog.service';
import { faCirclePlus, faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { UserModel } from '../../../../../../core/models/user/user.model';
import { DropdownOption } from '../../../../../../shared/data-types/dropdown-option';
import { StatusHttpService } from '../../../../../../core/state/projects/status/status-http.service';
import { FormControlNameRecord } from '../../../../../../shared/data-types/form-types';
import { ProjectOrTaskType } from '../../../../../../shared/data-types/status-types';
import { take } from 'rxjs/operators';
import {
  dropdownOptionToEntity,
  entityToDropdownOption,
} from '../../../../../../shared/functions/dropdown-functions';
import { ProjectTaskService } from '../../services/project-task.service';
import { ProjectDataService } from '../../../state-and-data-handling/project-data.service';
import { generateDummyId } from '../../../../../../shared/functions/array-functions';
import { StatusModel } from '../../../../../../core/models/project/status.model';
import { assert } from '../../../../../../core/assert/assert';
import { UserService } from '../../../../../user/services/user.service';
import { ProjectTaskGridService } from '../../manage-project/project-task-grid/project-task-grid.service';
import { ProjectTaskGridRow } from '../../../../shared/project-detail-types';
import { DateTimeHelper } from '../../../../../../features/calendar/util/date-time-helper';
import { ExpenditureRateEntry } from '../../../../../../core/models/project/project-expenditure-entry.model';

@Component({
  selector: 'app-project-task-dialog',
  templateUrl: './project-task-dialog.component.html',
  standalone: false,
})
export class ProjectTaskDialogComponent implements OnInit {
  readonly userOperation = model.required<UserOperation>();
  readonly selectedIndex = model.required();
  readonly taskGridRow = model<ProjectTaskGridRow>(null);
  readonly dialogFooter = viewChild<TemplateRef<unknown>>('dialogFooter');

  // Expenditure settings
  readonly showHourlyExpenditureRateFormField = model(false);
  readonly showBudgetMonetaryPerTaskFormField = model(false);
  readonly showBudgetHoursPerTaskFormField = model(false);

  // Data
  readonly rateAndBudget = model([0, 0]); // rate and budget

  public taskForm: FormGroup;
  public users: readonly UserModel[] = [];
  public statusOptions: readonly StatusModel[] = [];
  public statusDropdownOptions: readonly DropdownOption[] = [];
  public readonly UserOperation = UserOperation;
  public readonly faCirclePlus = faCirclePlus;
  public readonly faPenToSquare = faPenToSquare;
  public readonly SecurityContext = SecurityContext;
  public readonly formControlNames: FormControlNameRecord = {
    TaskName: 'taskName',
    Assignee: 'assignee',
    Status: 'status',
    Billable: 'billable',
    BudgetPerTask: 'budgetPerTask',
    HoursPerTask: 'hoursPerTask',
  };

  // DI
  public readonly projectTaskGridService = inject(ProjectTaskGridService);
  private readonly statusHttpService = inject(StatusHttpService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly projectTaskService = inject(ProjectTaskService);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly shiveDialogService = inject(ShiveDialogService);

  ngOnInit(): void {
    // Load task dependent status.
    const unassignedUserModel = UserService.buildDefaultUser();
    this.initForm(unassignedUserModel);
    this.loadTaskStatus();
    this.users = [unassignedUserModel, ...this.users];
  }

  // Apply result of updated history rate entries.
  public readonly makeApplyResult = (entries: ExpenditureRateEntry[]) => {
    this.taskGridRow.update((oldGridRow) => {
      this.rateAndBudget.update((oldRateAndBudget) => {
        const newRate = entries[entries.length - 1].rate;
        const oldBudget = oldRateAndBudget[1];
        return [newRate, oldBudget];
      });
      return { ...oldGridRow, expenditureRateHistory: entries };
    });
  };

  public close(): void {
    this.shiveDialogService.close(UserOperation.Cancel);
  }

  public submit(): void {
    const taskName = this.taskForm.get(this.formControlNames.TaskName).value as string;
    const assignee = this.taskForm.get(this.formControlNames.Assignee).value as UserModel;
    const project = this.projectDataService.getProjectClone();
    const statusEntity = dropdownOptionToEntity(
      this.taskForm.get(this.formControlNames.Status).value,
    );
    const selectedStatusModel = this.statusOptions.find((status) => status.id === statusEntity.id);

    const budgetMonetary = +this.taskForm.get(this.formControlNames.BudgetPerTask).value;
    const time = this.taskForm.get(this.formControlNames.HoursPerTask).value as Date | null;
    const budgetSeconds = time ? DateTimeHelper.timeOfDayToSeconds(time) : null;

    const isBillable = this.taskForm.get(this.formControlNames.Billable).value;

    assert(!!selectedStatusModel, 'Status model could not be found.');

    let id: number;
    // if it is a new task, create a dummy id
    if (!this.taskGridRow().task) {
      id = generateDummyId([...project.tasks]);
    } else {
      id = this.taskGridRow().task.id;
    }
    const upsertTask = this.projectTaskService.createTask(
      id,
      taskName,
      selectedStatusModel,
      assignee,
      isBillable,
    );
    this.taskGridRow.update((old) => {
      return {
        ...old,
        budgetMonetary,
        budgetSeconds,
        task: upsertTask,
      };
    });

    this.shiveDialogService.close(this.taskGridRow());
  }

  private initForm(unassignedUserModel: UserModel): void {
    let taskName = null;
    let assignee = unassignedUserModel;
    const isBillable = !!this.taskGridRow().task?.isBillable;

    if (this.userOperation() === UserOperation.Update) {
      taskName = this.taskGridRow().task.name;
      assignee = this.taskGridRow().task.assignee;
    }

    let timeForPicker = new Date();
    if (this.showBudgetHoursPerTaskFormField) {
      const seconds = this.taskGridRow().budgetSeconds;
      timeForPicker = DateTimeHelper.secondsToTimeOfDay(seconds);
    }

    this.taskForm = this.formBuilder.group({
      [this.formControlNames.TaskName]: [taskName, Validators.required],
      [this.formControlNames.Assignee]: [assignee, Validators.required],
      [this.formControlNames.Status]: [null, Validators.required],
      [this.formControlNames.Billable]: [isBillable, Validators.required],
      [this.formControlNames.BudgetPerTask]: [
        this.taskGridRow().budgetMonetary,
        Validators.required,
      ],
      [this.formControlNames.HoursPerTask]: [timeForPicker, Validators.required],
    });
  }

  private loadTaskStatus(): void {
    const status$ = this.statusHttpService.getStatus(ProjectOrTaskType.Task).pipe(take(1));
    status$.pipe(take(1)).subscribe((statusOptions) => {
      this.statusOptions = statusOptions;
      const selectedStatus = this.taskGridRow().task?.status.id
        ? this.statusOptions.find((option) => option.id === this.taskGridRow().task.status.id)
        : this.statusOptions[0];
      const selectedOption = entityToDropdownOption(selectedStatus);
      this.taskForm.controls.status.setValue(selectedOption);

      this.statusDropdownOptions = statusOptions.map((option) => entityToDropdownOption(option));
    });
  }
}
